import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Drawer, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  useGetApiServiceDetailQuery,
  useGetApiServiceMetricsRolling24hQuery,
} from '../../../../redux/api';
import { formatCustomUrlPublicPreview } from '../apiServiceApiHelpers';
import { K } from '../apiServiceI18n';
import { buildApiServiceBaseUrlPreview, submitTransactionSearch, mapApiServiceDetailResponse } from '../utils/apiServiceUtils';
import ApiServicePerformanceSingularToolbar from './ApiServicePerformanceSingularToolbar';
import ApiServiceStackedColumnChart from './ApiServiceStackedColumnChart';
import {
  DEFAULT_PERF_FILTERS,
  TIMEFRAME_1D,
  TIMEFRAME_CUSTOM,
  defaultCustomRange,
  formatDateParam,
  metricsSuccess,
  rangeForPreset,
  transformSingularTransactionsBySource,
  transformSingularViolationsBySource,
  resolveSingularAccount,
  hasSingularWorkflowContext,
  isMetricsQueryPending,
  enrichSingularBySourceRows,
  txnSearchRangeForTimeframe,
} from './apiServicePerformanceUtils';
import ApiServicePerformanceLoader from './ApiServicePerformanceLoader';

function ApiServicePerformanceSingularView({ target, onClose }) {
  const { t } = useTranslation();
  const { account: reduxAccount, baseUrl } = useSelector((state) => state.queryParams);

  const [timeframe, setTimeframe] = useState(TIMEFRAME_1D);
  const [customRange, setCustomRange] = useState(defaultCustomRange);
  const [quantity, setQuantity] = useState(DEFAULT_PERF_FILTERS.quantity);

  const account = useMemo(
    () => resolveSingularAccount(reduxAccount, target?.account),
    [reduxAccount, target?.account]
  );

  const canFetch = !!target && !!account && hasSingularWorkflowContext(target);

  const serviceDetailQuery = useGetApiServiceDetailQuery(
    {
      account,
      wfg_name: target?.workflowGroup,
      wf_name: target?.workflowName,
    },
    { skip: !canFetch }
  );

  const { detail } = useMemo(
    () => mapApiServiceDetailResponse(serviceDetailQuery.data),
    [serviceDetailQuery.data]
  );

  const baseUrlPreview = useMemo(() => {
    if (!detail) return '';
    const preview = buildApiServiceBaseUrlPreview({
      account: detail.account || account,
      workflowGroup: detail.workflowGroup || target?.workflowGroup,
      workflowName: detail.workflowName || target?.workflowName,
      pathBase: detail.baseUrl,
    });
    return preview || '';
  }, [detail, account, target]);

  const customUrlPreview = useMemo(() => {
    if (!detail?.customUrl) return '';
    return formatCustomUrlPublicPreview(detail.customUrl);
  }, [detail]);

  useEffect(() => {
    if (!target) return;
    setTimeframe(TIMEFRAME_1D);
    setCustomRange(defaultCustomRange());
    setQuantity(DEFAULT_PERF_FILTERS.quantity);
  }, [target?.workflowGroup, target?.workflowName, target?.account]);

  const effectiveRange = useMemo(() => {
    if (timeframe === TIMEFRAME_CUSTOM) return customRange;
    return rangeForPreset(timeframe);
  }, [timeframe, customRange]);

  const rollingParams = useMemo(() => {
    const params = {
      account,
      wfg_name: target?.workflowGroup,
      wf_name: target?.workflowName,
      include_by_source: true,
      rowcnt: Math.min(500, quantity * 20),
    };
    if (timeframe !== TIMEFRAME_1D) {
      params.start_date = formatDateParam(effectiveRange.from);
      params.end_date = formatDateParam(effectiveRange.to);
    }
    return params;
  }, [account, target?.workflowGroup, target?.workflowName, quantity, timeframe, effectiveRange]);

  const rollingQuery = useGetApiServiceMetricsRolling24hQuery(
    canFetch ? rollingParams : skipToken,
    { refetchOnMountOrArgChange: true }
  );

  const rollingPayload = rollingQuery.currentData ?? rollingQuery.data;
  const singularLoading = isMetricsQueryPending(rollingQuery, rollingPayload);

  const bySourceRows = useMemo(() => {
    if (!metricsSuccess(rollingPayload)) return [];
    return enrichSingularBySourceRows(rollingPayload?.data?.by_source || [], target);
  }, [rollingPayload, target]);

  const txnSearchRange = useMemo(
    () => txnSearchRangeForTimeframe(timeframe, effectiveRange),
    [timeframe, effectiveRange]
  );

  const txnChart = useMemo(
    () =>
      transformSingularTransactionsBySource(bySourceRows, quantity, {
        conforming: t(K.API_SVC_PERF_CONFORMING),
        nonConforming: t(K.API_SVC_PERF_NON_CONFORMING),
      }),
    [bySourceRows, quantity, t]
  );

  const violChart = useMemo(
    () =>
      transformSingularViolationsBySource(bySourceRows, quantity, {
        execTime: t(K.API_SVC_PERF_EXEC_TIME),
        execTimeTooltip: t(K.API_SVC_PERF_EXEC_TIME_ERRORS),
        validation: t(K.API_SVC_PERF_VALIDATION),
        translation: t(K.API_SVC_PERF_TRANSLATION),
        errors: t(K.API_SVC_PERF_ERRORS),
      }),
    [bySourceRows, quantity, t]
  );

  const openTransactionSearch = useCallback(
    (bar) => {
      submitTransactionSearch(baseUrl, {
        account,
        workflowGroup: target?.workflowGroup || bar.workflowGroup,
        workflowName: target?.workflowName || bar.workflowName,
        startDate: txnSearchRange.from,
        endDate: txnSearchRange.to,
        source: bar.sourceKey || bar.label,
      });
    },
    [baseUrl, account, target, txnSearchRange]
  );

  return (
    <Drawer
      anchor="left"
      open={!!target}
      onClose={onClose}
      PaperProps={{ sx: { width: '90%', maxWidth: '90vw' } }}
    >
      <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            {target?.label}
          </Typography>
          <IconButton onClick={onClose} aria-label={t(K.API_SVC_CLOSE_SINGULAR_VIEW)}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <ApiServicePerformanceSingularToolbar
          timeframe={timeframe}
          onTimeframeChange={setTimeframe}
          customRange={customRange}
          onCustomRangeChange={setCustomRange}
          quantity={quantity}
          onQuantityChange={setQuantity}
          baseUrl={detail?.baseUrl?.trim() ? baseUrlPreview : ''}
          customUrl={customUrlPreview}
        />

        {target && !canFetch && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t(K.API_SVC_PERF_SINGULAR_MISSING_CONTEXT)}
          </Alert>
        )}

        {rollingQuery.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t(K.API_SVC_PERF_LOAD_FAILED)}
          </Alert>
        )}

        {canFetch && singularLoading && !rollingQuery.error ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ApiServicePerformanceLoader
              variant="inline"
              message={t(K.API_SVC_PERF_LOADING)}
              minHeight={320}
            />
          </Box>
        ) : (
          <Stack spacing={3} sx={{ overflow: 'auto', flex: 1, position: 'relative' }}>
            {canFetch && rollingQuery.isFetching && bySourceRows.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  bgcolor: 'rgba(255,255,255,0.55)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 2,
                }}
              >
                <CircularProgress size={28} />
              </Box>
            )}

            <ApiServiceStackedColumnChart
            title={t(K.API_SVC_PERF_SINGULAR_TXN_CHART)}
            yAxisLabel={t(K.API_SVC_PERF_Y_TRANSACTIONS)}
            xAxisLabel={t(K.API_SVC_PERF_X_SOURCE)}
            series={txnChart.series}
            bars={txnChart.bars}
            loading={false}
            loadingMessage={t(K.API_SVC_PERF_LOADING)}
            emptyMessage={t(K.API_SVC_PERF_NO_DATA)}
            onBarClick={openTransactionSearch}
            renderTooltip={({ bar, series, value }) => (
              <>
                <div>
                  <strong>{bar.label}</strong>
                </div>
                <div>
                  {series.label}: {value}
                </div>
                <div>
                  {t(K.API_SVC_PERF_TOTAL_REQUESTS)}: {bar.total}
                </div>
              </>
            )}
          />

          <ApiServiceStackedColumnChart
            title={t(K.API_SVC_PERF_SINGULAR_VIOL_CHART)}
            yAxisLabel={t(K.API_SVC_PERF_Y_VIOLATIONS)}
            xAxisLabel={t(K.API_SVC_PERF_X_SOURCE)}
            series={violChart.series}
            bars={violChart.bars}
            loading={false}
            loadingMessage={t(K.API_SVC_PERF_LOADING)}
            emptyMessage={t(K.API_SVC_PERF_NO_OUTLIERS)}
            onBarClick={openTransactionSearch}
            renderTooltip={({ bar, series, value }) => (
              <>
                <div>
                  {t(K.API_SVC_PERF_TOTAL_ERRORS)}: {bar.total}
                </div>
                <div>
                  {series.tooltipLabel || series.label}: {value}
                </div>
              </>
            )}
          />
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}

export default memo(ApiServicePerformanceSingularView);
